import mitt from 'mitt';
import * as SIP from 'sip.js';

const CONNECTION_STATUS = Object.freeze({
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
});

const CALL_STATE = Object.freeze({
  IDLE: 'idle',
  RINGING: 'ringing',
  CONNECTED: 'connected',
});

class SipClient {
  static instance = null;

  static getInstance() {
    if (!SipClient.instance) {
      SipClient.instance = new SipClient();
    }
    return SipClient.instance;
  }

  constructor() {
    this.emitter = mitt();

    this.userAgent = null;
    this.registerer = null;
    this.currentSession = null;

    this.remoteAudioEl = null;
    this.remoteStream = null;

    this.connectionStatus = CONNECTION_STATUS.DISCONNECTED;
    this.isRegistered = false;

    this.shouldBeConnected = false;
    this.attemptingReconnection = false;
    this.reconnectAttempt = 0;
    this.reconnectTimer = null;
    this.registerRetryTimer = null;

    this.callTimerInterval = null;
    this.callStartTime = null;
    this.isMuted = false;
    this.currentDirection = null; // 'inbound' | 'outbound'
    this.currentRemoteNumber = null;
    this.endedReason = null;
    this.lastSipStatusCode = null;
    this.lastSipReasonPhrase = null;

    // local mic (optional): used to validate mic access and help debugging
    this.localStream = null;
    this._micWarmupPromise = null;

    // ringback (outbound)
    this._ringback = {
      ctx: null,
      oscA: null,
      oscB: null,
      gain: null,
      timer: null,
      isPlaying: false,
    };

    // remote audio wiring guard (avoid double-binding)
    this._remoteAudioBoundPc = null;

    // avoid duplicate call:ended emissions when both onReject and Terminated fire
    this._callEndedForSessionId = null;
  }

  on(eventName, handler) {
    this.emitter.on(eventName, handler);
    return () => this.emitter.off(eventName, handler);
  }

  emit(eventName, payload) {
    this.emitter.emit(eventName, payload);
  }

  setRemoteAudioElement(audioEl) {
    this.remoteAudioEl = audioEl || null;
  }

  unlockAudioPlayback() {
    if (!this.remoteAudioEl) return;
    try {
      this.remoteAudioEl.muted = true;
      const p = this.remoteAudioEl.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          if (this.remoteAudioEl) this.remoteAudioEl.muted = false;
        }).catch(() => {});
      } else {
        this.remoteAudioEl.muted = false;
      }
    } catch {
      // noop
    }
  }

  startRingback() {
    if (this._ringback.isPlaying) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    try {
      const ctx = new AudioCtx();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(ctx.destination);

      // Typical ringback: mix of 440Hz + 480Hz, 2s ON / 4s OFF
      const oscA = ctx.createOscillator();
      oscA.type = 'sine';
      oscA.frequency.value = 440;
      oscA.connect(gain);

      const oscB = ctx.createOscillator();
      oscB.type = 'sine';
      oscB.frequency.value = 480;
      oscB.connect(gain);

      oscA.start();
      oscB.start();

      const pattern = () => {
        if (!this._ringback.isPlaying) return;
        // ON
        gain.gain.setTargetAtTime(0.08, ctx.currentTime, 0.01);
        this._ringback.timer = setTimeout(() => {
          // OFF
          gain.gain.setTargetAtTime(0.0, ctx.currentTime, 0.01);
          this._ringback.timer = setTimeout(pattern, 4000);
        }, 2000);
      };

      this._ringback = {
        ctx,
        oscA,
        oscB,
        gain,
        timer: null,
        isPlaying: true,
      };

      // Resume if needed (should be a user gesture on click-to-call).
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      pattern();
    } catch {
      // noop
    }
  }

  stopRingback() {
    if (!this._ringback.isPlaying) return;
    try {
      if (this._ringback.timer) clearTimeout(this._ringback.timer);
      if (this._ringback.gain && this._ringback.ctx) {
        this._ringback.gain.gain.setTargetAtTime(0.0, this._ringback.ctx.currentTime, 0.01);
      }
      if (this._ringback.oscA) this._ringback.oscA.stop();
      if (this._ringback.oscB) this._ringback.oscB.stop();
      if (this._ringback.ctx) this._ringback.ctx.close().catch(() => {});
    } catch {
      // ignore
    } finally {
      this._ringback = {
        ctx: null,
        oscA: null,
        oscB: null,
        gain: null,
        timer: null,
        isPlaying: false,
      };
    }
  }

  bindRemoteAudio(session) {
    const pc = session?.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;
    if (this._remoteAudioBoundPc === pc) return;
    this._remoteAudioBoundPc = pc;

    this.remoteStream = new MediaStream();
    const attachRemoteStream = stream => {
      if (!this.remoteAudioEl) return;
      this.remoteAudioEl.srcObject = stream;
      this.unlockAudioPlayback();
    };

    pc.getReceivers().forEach(r => {
      if (r.track) this.remoteStream.addTrack(r.track);
    });
    attachRemoteStream(this.remoteStream);

    pc.ontrack = event => {
      if (event.streams?.[0]) {
        attachRemoteStream(event.streams[0]);
        return;
      }
      if (event.track && this.remoteStream) {
        this.remoteStream.addTrack(event.track);
        attachRemoteStream(this.remoteStream);
      }
    };
  }

  mapSipStatusToReason(statusCode) {
    const code = Number(statusCode);
    if (!code) return 'failed';
    if (code === 487) return 'cancelled';
    if (code === 603) return 'rejected';
    if (code === 486 || code === 600) return 'busy';
    if (code === 408 || code === 480) return 'no_answer';
    if (code >= 300) return 'failed';
    return 'terminated';
  }

  emitCallEndedOnce({ session, durationSec }) {
    const sessionId = session?.id || null;
    if (sessionId && this._callEndedForSessionId === sessionId) return;
    if (sessionId) this._callEndedForSessionId = sessionId;

    this.emit('call:ended', {
      direction: this.currentDirection,
      remoteNumber: this.currentRemoteNumber,
      durationSec: typeof durationSec === 'number' ? durationSec : 0,
      reason: this.endedReason || 'terminated',
      sipStatusCode: this.lastSipStatusCode,
      sipReasonPhrase: this.lastSipReasonPhrase,
    });
  }

  isLocalhostLike(hostname) {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
  }

  getUserMediaCompat(constraints) {
    const md = navigator.mediaDevices;
    if (md?.getUserMedia) return md.getUserMedia(constraints);
    const legacy =
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia;
    if (!legacy) return null;
    return new Promise((resolve, reject) => legacy.call(navigator, constraints, resolve, reject));
  }

  getMicrophoneCapability() {
    const hasGetUserMedia =
      (!!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function') ||
      typeof navigator.getUserMedia === 'function' ||
      typeof navigator.webkitGetUserMedia === 'function' ||
      typeof navigator.mozGetUserMedia === 'function' ||
      typeof navigator.msGetUserMedia === 'function';

    // En HTTP (no localhost), Chrome/Edge bloquean micrófono. Esto explica el síntoma:
    // - Tú escuchas al remoto (recv funciona)
    // - El remoto no te escucha (no hay track de salida)
    const secureOk = !!window.isSecureContext || this.isLocalhostLike(window.location.hostname);

    return { hasGetUserMedia, secureOk };
  }

  async ensureMicrophonePermission() {
    const { hasGetUserMedia, secureOk } = this.getMicrophoneCapability();
    if (!hasGetUserMedia) {
      throw new Error('No hay soporte de micrófono (getUserMedia) en este navegador.');
    }
    if (!secureOk) {
      throw new Error('El micrófono requiere HTTPS (contexto seguro). Abre Chatwoot en https:// o en localhost.');
    }

    const p = this.getUserMediaCompat({ audio: true, video: false });
    if (!p) throw new Error('No hay soporte de micrófono (getUserMedia) en este navegador.');

    try {
      // Guardamos stream para debug/estado (y para que el permiso quede concedido).
      // OJO: no lo cerramos aquí para evitar que algunos navegadores “duerman” el input inmediatamente.
      // Se libera en cleanupCall().
      this.localStream = await p;
      return true;
    } catch (e) {
      const name = e?.name || '';
      if (name === 'SecurityError') {
        throw new Error('El navegador bloqueó el micrófono por políticas de seguridad (HTTPS/Permissions-Policy).');
      }
      const suffix = name ? ` (${name})` : '';
      throw new Error(`Permiso de micrófono denegado o no disponible${suffix}.`);
    }
  }

  setConnectionStatus(status, error) {
    this.connectionStatus = status;
    this.emit('connection:status', { status, error });
  }

  setRegistered(isRegistered) {
    this.isRegistered = isRegistered;
    this.emit('registration:status', {
      status: isRegistered ? 'registered' : 'unregistered',
    });
  }

  clearReconnectTimers() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.registerRetryTimer) {
      clearTimeout(this.registerRetryTimer);
      this.registerRetryTimer = null;
    }
  }

  scheduleRegisterRetry(delaySeconds = 2) {
    if (!this.shouldBeConnected || !this.registerer) return;
    if (this.registerRetryTimer) return;
    const delayMs = Math.max(0, delaySeconds * 1000);
    this.registerRetryTimer = setTimeout(async () => {
      this.registerRetryTimer = null;
      if (!this.shouldBeConnected || !this.registerer) return;
      try {
        await this.registerer.register();
      } catch (e) {
        const retryAfter = this.registerer?.retryAfter;
        this.scheduleRegisterRetry(typeof retryAfter === 'number' ? retryAfter : 4);
      }
    }, delayMs);
  }

  scheduleReconnect() {
    if (!this.shouldBeConnected || !this.userAgent) return;
    if (this.attemptingReconnection) return;
    this.attemptingReconnection = true;

    const attempt = ++this.reconnectAttempt;
    const base = 2;
    const max = 30;
    const delaySeconds = Math.min(max, attempt === 1 ? 0 : base * Math.pow(1.5, attempt - 2));

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      if (!this.shouldBeConnected || !this.userAgent) {
        this.attemptingReconnection = false;
        return;
      }
      try {
        await Promise.resolve().then(() => this.userAgent.reconnect());
        this.attemptingReconnection = false;
        this.scheduleRegisterRetry(0);
      } catch (e) {
        this.attemptingReconnection = false;
        this.scheduleReconnect();
      }
    }, delaySeconds * 1000);
  }

  async connect({ sip_ws_url: sipWsUrl, extension, auth_user: authUser, auth_password: authPassword } = {}) {
    if (!sipWsUrl || !extension) {
      throw new Error('Config SIP incompleta: sip_ws_url/extension requeridos');
    }
    if (!authPassword) {
      throw new Error('Config SIP incompleta: auth_password requerido');
    }

    // En adelante, mantenemos intención de conexión
    this.shouldBeConnected = true;
    this.reconnectAttempt = 0;
    this.clearReconnectTimers();
    this.setConnectionStatus(CONNECTION_STATUS.CONNECTING);

    // Si existe un UA previo, lo apagamos sin perder la intención de conexión.
    await this.disconnect({ preserveIntent: true });

    const wsUrl = new URL(sipWsUrl);
    const domain = wsUrl.hostname;
    const uri = SIP.UserAgent.makeURI(`sip:${extension}@${domain}`);
    if (!uri) throw new Error('No se pudo construir URI SIP');

    const uaOptions = {
      uri,
      transportOptions: {
        server: sipWsUrl,
        keepAliveInterval: 30,
        keepAliveDebounce: 10,
        connectionTimeout: 10,
        traceSip: false,
      },
      authorizationUsername: (authUser || '').trim() || extension,
      authorizationPassword: authPassword,
      displayName: `Agente ${extension}`,
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: {
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        },
      },
      delegate: {
        onInvite: invitation => this.handleIncomingCall(invitation),
        onConnect: () => {
          // transporte re-conectó
          this.scheduleRegisterRetry(0);
        },
        onDisconnect: error => {
          this.setRegistered(false);
          if (this.shouldBeConnected) {
            this.setConnectionStatus(CONNECTION_STATUS.CONNECTING, error);
            this.scheduleReconnect();
          } else {
            this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED, error);
          }
        },
      },
    };

    this.userAgent = new SIP.UserAgent(uaOptions);
    await this.userAgent.start();

    this.registerer = new SIP.Registerer(this.userAgent, {
      expires: 600,
      refreshFrequency: 99,
    });

    this.registerer.stateChange.addListener(newState => {
      if (newState === SIP.RegistererState.Registered) {
        this.setRegistered(true);
        this.setConnectionStatus(CONNECTION_STATUS.CONNECTED);
      } else if (newState === SIP.RegistererState.Unregistered) {
        this.setRegistered(false);
        if (this.shouldBeConnected) {
          this.setConnectionStatus(CONNECTION_STATUS.CONNECTING);
          const retryAfter = this.registerer?.retryAfter;
          this.scheduleRegisterRetry(typeof retryAfter === 'number' ? retryAfter : 2);
          this.scheduleReconnect();
        } else {
          this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        }
      }
    });

    try {
      await this.registerer.register();
    } catch (e) {
      this.setConnectionStatus(CONNECTION_STATUS.ERROR, e);
      throw e;
    }

    // online hook: si vuelve red, reconectar
    if (!this._onlineHandler) {
      this._onlineHandler = () => {
        if (this.shouldBeConnected) this.scheduleReconnect();
      };
      window.addEventListener('online', this._onlineHandler);
    }
  }

  async disconnect({ preserveIntent = false } = {}) {
    if (!preserveIntent) this.shouldBeConnected = false;
    this.clearReconnectTimers();

    this.cleanupCall();

    try {
      if (this.registerer) {
        try {
          await this.registerer.unregister();
        } catch {
          // ignore
        }
      }
    } finally {
      this.registerer = null;
    }

    try {
      if (this.userAgent) {
        try {
          await this.userAgent.stop();
        } catch {
          // ignore
        }
      }
    } finally {
      this.userAgent = null;
    }

    this.setRegistered(false);
    this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
  }

  handleIncomingCall(invitation) {
    if (this.currentSession) {
      this.rejectInboundInvitation(invitation);
      return;
    }
    this.currentSession = invitation;

    const caller = invitation?.remoteIdentity?.uri?.user || 'Desconocido';
    const callTypeHeader = invitation?.request?.getHeader?.('X-Chatwoot-Call-Type');
    const didHeader = invitation?.request?.getHeader?.('X-Chatwoot-DID');
    const callIdHeader = invitation?.request?.getHeader?.('X-Chatwoot-Call-Id');
    const callType = (callTypeHeader || 'sip').toString().trim().toLowerCase() || 'sip';
    const didNumber = (didHeader || '').toString().trim() || null;
    const callId = (callIdHeader || '').toString().trim() || null;
    this.currentDirection = 'inbound';
    this.currentRemoteNumber = caller;
    this.emit('call:incoming', { remoteNumber: caller, callType, callId, didNumber });
    this.emit('call:state', {
      state: CALL_STATE.RINGING,
      remoteNumber: caller,
      direction: this.currentDirection,
    });

    // Pre-calentar micrófono mientras timbra para que "Contestar" sea inmediato.
    // No bloquea UI; si falla, se reintentará al contestar.
    if (!this._micWarmupPromise) {
      this._micWarmupPromise = this.ensureMicrophonePermission().catch(() => null);
    }

    this.setupSession(invitation);
  }

  async dial(target, { didNumber, callType, callId } = {}) {
    const number = (target || '').toString().trim();
    if (!number) throw new Error('Número inválido');
    if (!this.userAgent) throw new Error('SIP no conectado');
    if (!this.isRegistered) throw new Error('SIP no registrado');
    if (this.currentSession) throw new Error('Ya hay una llamada activa');

    const wsUrl = new URL(this.userAgent.configuration.transportOptions.server);
    const domain = wsUrl.hostname;
    const targetUri = SIP.UserAgent.makeURI(`sip:${number}@${domain}`);
    if (!targetUri) throw new Error('No se pudo construir URI destino');

    // earlyMedia=true permite reproducir audio remoto en respuestas provisionales (183+SDP),
    // útil para ringback real desde Asterisk.
    //
    // IMPORTANTE (sip.js):
    // Los headers del INVITE inicial deben ir en `InviterOptions.extraHeaders` (constructor),
    // no en `invite({ requestOptions })`, para que Asterisk los reciba (incluyendo el retry tras 401).
    const normalizedCallType = (callType || 'sip').toString().trim().toLowerCase() || 'sip';
    const inviteHeaders = [];
    const normalizedCallId = (callId || '').toString().trim();
    if (didNumber) inviteHeaders.push(`X-Chatwoot-DID: ${didNumber}`);
    inviteHeaders.push(`X-Chatwoot-Call-Type: ${normalizedCallType}`);
    if (normalizedCallId) inviteHeaders.push(`X-Chatwoot-Call-Id: ${normalizedCallId}`);

    const inviter = new SIP.Inviter(this.userAgent, targetUri, {
      earlyMedia: true,
      extraHeaders: inviteHeaders,
    });
    this.currentSession = inviter;
    this.currentDirection = 'outbound';
    this.currentRemoteNumber = number;
    this.endedReason = null;
    this.lastSipStatusCode = null;
    this.lastSipReasonPhrase = null;
    this._callEndedForSessionId = null;
    this.emit('call:state', {
      state: CALL_STATE.RINGING,
      remoteNumber: number,
      direction: this.currentDirection,
    });
    this.setupSession(inviter);

    this.unlockAudioPlayback();
    try {
      // Si no hay micrófono (p.ej. HTTP), sip.js puede terminar negociando recvonly/silencio.
      // Validamos explícitamente para evitar "me oyen en silencio" y para que mute tenga track.
      await this.ensureMicrophonePermission();
      // ringback local (fallback). Si llega early media, lo detenemos.
      this.startRingback();
      await inviter.invite({
        requestDelegate: {
          onProgress: response => {
            const status = response?.message?.statusCode;
            // 180 Ringing: mantenemos ringback local
            if (status === 180) {
              this.startRingback();
            }
            // 183 Session Progress con SDP: early media (ringback remoto)
            if (status === 183 && response?.message?.body) {
              this.stopRingback();
              this.bindRemoteAudio(inviter);
            }
          },
          onReject: response => {
            const statusCode = response?.message?.statusCode || null;
            const reasonPhrase = response?.message?.reasonPhrase || null;
            this.lastSipStatusCode = statusCode;
            this.lastSipReasonPhrase = reasonPhrase;
            this.endedReason = this.mapSipStatusToReason(statusCode);
            this.stopRingback();

            // IMPORTANTE:
            // inviter.invite() se resuelve al enviar INVITE; si el remoto cuelga/rechaza antes de contestar,
            // necesitamos cerrar la UI aquí mismo (sin depender solo de SessionState.Terminated).
            this.emitCallEndedOnce({ session: inviter, durationSec: 0 });
            this.cleanupCall();
          },
          onAccept: () => {
            this.stopRingback();
          },
        },
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
      return true;
    } catch (e) {
      this.cleanupCall();
      throw e;
    }
  }

  async answer() {
    if (!this.currentSession) return;
    // Feedback inmediato: detener timbrado local y desbloquear audio.
    this.stopRingback();
    this.unlockAudioPlayback();
    if (this._micWarmupPromise) {
      await this._micWarmupPromise;
    } else {
      await this.ensureMicrophonePermission();
    }
    await this.currentSession.accept({
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });
  }

  hangup() {
    if (!this.currentSession) return;
    this.endedReason = this.endedReason || 'hangup';
    const session = this.currentSession;
    if (session.state === SIP.SessionState.Established) {
      session.bye();
    } else if (this.currentDirection === 'inbound' && typeof session.reject === 'function') {
      // For inbound ringing calls, send an explicit final decline so upstream
      // does not keep retrying/ringing the same call attempt.
      this.endedReason = 'rejected';
      this.rejectInboundInvitation(session);
    } else if (typeof session.cancel === 'function') {
      session.cancel();
    } else if (typeof session.reject === 'function') {
      session.reject();
    }
  }

  rejectInboundInvitation(invitation) {
    invitation.reject({
      statusCode: 603,
      reasonPhrase: 'Decline',
    });
  }

  toggleMute() {
    if (!this.currentSession) return false;
    const pc = this.currentSession.sessionDescriptionHandler?.peerConnection;
    if (!pc) return false;

    const nextMuted = !this.isMuted;
    const senders = pc.getSenders();
    const audioSenders = senders.filter(s => s.track?.kind === 'audio');
    if (audioSenders.length === 0) {
      return false;
    }

    audioSenders.forEach(sender => {
      if (sender.track?.kind === 'audio') {
        sender.track.enabled = !nextMuted;
      }
    });
    this.isMuted = nextMuted;
    this.emit('call:muted', { isMuted: this.isMuted });
    return this.isMuted;
  }

  setupSession(session) {
    session.stateChange.addListener(newState => {
      if (newState === SIP.SessionState.Established) {
        const remoteNumber =
          this.currentSession?.remoteIdentity?.uri?.user ||
          this.currentSession?.remoteIdentity?.displayName ||
          null;

        if (remoteNumber) this.currentRemoteNumber = remoteNumber;
        this.emit('call:state', {
          state: CALL_STATE.CONNECTED,
          remoteNumber: this.currentRemoteNumber,
          direction: this.currentDirection,
        });
        this.stopRingback();
        this.startCallTimer();

        this.bindRemoteAudio(session);
      }

      if (newState === SIP.SessionState.Terminated) {
        const endedAt = Date.now();
        const durationSec = this.callStartTime
          ? Math.max(0, Math.floor((endedAt - this.callStartTime) / 1000))
          : 0;
        this.stopRingback();
        this.emitCallEndedOnce({ session, durationSec });
        this.cleanupCall();
      }
    });
  }

  startCallTimer() {
    this.callStartTime = Date.now();
    if (this.callTimerInterval) clearInterval(this.callTimerInterval);
    this.callTimerInterval = setInterval(() => {
      const durationSec = Math.max(0, Math.floor((Date.now() - this.callStartTime) / 1000));
      this.emit('call:duration', { durationSec });
    }, 1000);
  }

  cleanupCall() {
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
    this.callStartTime = null;
    this.remoteStream = null;
    this.isMuted = false;
    this.stopRingback();
    this._remoteAudioBoundPc = null;
    this._callEndedForSessionId = null;
    this._micWarmupPromise = null;

    // Liberar micrófono si lo habíamos solicitado explícitamente
    if (this.localStream) {
      try {
        this.localStream.getTracks().forEach(t => t.stop());
      } catch {
        // ignore
      }
      this.localStream = null;
    }
    this.currentDirection = null;
    this.currentRemoteNumber = null;
    this.endedReason = null;
    this.lastSipStatusCode = null;
    this.lastSipReasonPhrase = null;

    this.currentSession = null;
    this.emit('call:muted', { isMuted: false });
    this.emit('call:state', { state: CALL_STATE.IDLE });
    this.emit('call:duration', { durationSec: 0 });
  }

  getStatus() {
    return {
      connectionStatus: this.connectionStatus,
      isRegistered: this.isRegistered,
      hasUserAgent: !!this.userAgent,
      hasSession: !!this.currentSession,
    };
  }
}

export default SipClient;

